<template>
  <div class="scc-root">
    <!-- Sidebar -->
    <aside class="scc-sidebar" :class="{ 'scc-sidebar--collapsed': sidebarCollapsed }">
      <!-- Sidebar Brand -->
      <div class="scc-sidebar-brand">
        <div class="scc-sidebar-brand-icon">
          <v-icon color="white" size="20">mdi-creation</v-icon>
        </div>
        <span class="scc-sidebar-brand-text">SAC Composer Chat</span>
      </div>

      <!-- New Chat Button -->
      <div class="scc-sidebar-new">
        <button class="scc-new-chat-btn" @click="createNewChat">
          <v-icon small color="white" class="mr-2">mdi-plus-circle-outline</v-icon>
          New conversation
        </button>
      </div>

      <!-- Chat History -->
      <div class="scc-chat-list">
        <div class="scc-chat-list-label">Recent</div>
        <div
          v-for="(chat, index) in chats"
          :key="index"
          class="scc-chat-item"
          :class="{ 'scc-chat-item--active': currentChatIndex === index }"
          :title="chat.title || 'New chat'"
          @click="selectChat(index)"
        >
          <v-icon small class="scc-chat-item-icon">mdi-message-text-outline</v-icon>
          <div class="scc-chat-item-text">
            <span class="scc-chat-item-title" :title="chat.title || 'New chat'">{{
              chat.title || 'New chat'
            }}</span>
            <span v-if="chat.createdAt" class="scc-chat-item-date">{{
              formatDate(chat.createdAt)
            }}</span>
          </div>
          <button class="scc-chat-item-delete" @click.stop="deleteChat(index)" title="Delete chat">
            <v-icon small color="red">mdi-trash-can-outline</v-icon>
          </button>
        </div>
      </div>

      <!-- Sidebar Footer -->
      <div class="scc-sidebar-footer">
        <v-avatar size="28" class="scc-sidebar-footer-avatar">
          <img
            alt="Avatar"
            :src="require('@/assets/avatars/Frame-' + $store.getters.avatar + '.svg')"
          />
        </v-avatar>
        <span class="scc-sidebar-footer-name">{{ userName }}</span>
      </div>
    </aside>

    <!-- Main Area -->
    <main class="scc-main">
      <!-- Top Bar -->
      <div class="scc-topbar">
        <Breadcrumbs />
      </div>

      <!-- Messages -->
      <div class="scc-messages" ref="messagesContainer">
        <!-- Conversation -->
        <div v-if="currentChat && currentChat.messages.length > 0" class="scc-messages-inner">
          <div
            v-for="(message, index) in currentChat.messages"
            :key="index"
            class="scc-msg"
            :class="'scc-msg--' + message.role"
          >
            <div class="scc-msg-row">
              <v-avatar
                size="32"
                class="scc-msg-avatar"
                :class="message.role === 'user' ? 'scc-msg-avatar--user' : 'scc-msg-avatar--ai'"
              >
                <img
                  v-if="message.role === 'user'"
                  alt="Avatar"
                  :src="require('@/assets/avatars/Frame-' + $store.getters.avatar + '.svg')"
                />
                <v-icon v-else dark size="18">mdi-creation</v-icon>
              </v-avatar>
              <div class="scc-msg-body">
                <div class="scc-msg-author">
                  {{ message.role === 'user' ? userName : 'SAC Composer' }}
                </div>
                <div class="scc-msg-content">
                  <div v-if="message.thinking" class="scc-thinking">
                    <span class="scc-thinking-dot"></span>
                    <span class="scc-thinking-dot"></span>
                    <span class="scc-thinking-dot"></span>
                  </div>
                  <div v-else v-html="formatMessage(message.content)"></div>
                </div>
              </div>
            </div>

            <!-- HITL Actions -->
            <div
              v-if="message.hitlPending && index === currentChat.messages.length - 1"
              class="scc-hitl-actions"
            >
              <div class="scc-hitl-banner">
                <v-icon small color="#f57c00" class="mr-2">mdi-alert-circle-outline</v-icon>
                <span
                  >The assistant needs your confirmation to proceed with the proposed action.</span
                >
              </div>
              <div class="scc-hitl-btns">
                <button
                  class="scc-hitl-btn scc-hitl-btn--approve"
                  :disabled="isSending"
                  @click="handleHitlAction('approve')"
                >
                  <v-icon small class="mr-1">mdi-check</v-icon>
                  Approve
                </button>
                <button
                  class="scc-hitl-btn scc-hitl-btn--reject"
                  :disabled="isSending"
                  @click="showHitlRejectInput = !showHitlRejectInput"
                >
                  <v-icon small class="mr-1">mdi-close</v-icon>
                  Reject
                </button>
              </div>
              <div v-if="showHitlRejectInput" class="scc-hitl-reject-input mt-2">
                <v-text-field
                  v-model="hitlExplanation"
                  dense
                  outlined
                  hide-details
                  placeholder="Reason for rejection (optional)"
                  class="mb-2"
                ></v-text-field>
                <button
                  class="scc-hitl-btn scc-hitl-btn--reject-confirm"
                  :disabled="isSending"
                  @click="handleHitlAction('reject')"
                >
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>

          <!-- Create SAC hint chip -->
          <div v-if="showCreateSacHint" class="scc-create-hint">
            <v-icon x-small color="#0097a7" class="mr-1">mdi-lightbulb-outline</v-icon>
            Happy with the plan? Click to build it:
            <button
              class="scc-suggestion-chip ml-2"
              @click="sendMessage('Create the Security Assurance Case')"
            >
              <v-icon x-small class="mr-1" color="#0097a7">mdi-shield-plus-outline</v-icon>
              Create the Security Assurance Case
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="scc-empty">
          <div class="scc-empty-inner">
            <div class="scc-empty-orb">
              <div class="scc-empty-orb-ring"></div>
              <v-icon size="48" class="scc-empty-orb-icon">mdi-creation</v-icon>
            </div>
            <h2 class="scc-empty-title">How can I help you today?</h2>
            <p class="scc-empty-subtitle">
              Ask me anything about your security assurance cases, requirements, or compliance.
            </p>
            <!-- <div class="scc-prompts">
              <button
                v-for="(prompt, i) in examplePrompts"
                :key="i"
                class="scc-prompt-card"
                @click="sendMessage(prompt)"
              >
                <v-icon small color="#0097a7" class="mr-2">mdi-lightbulb-outline</v-icon>
                <span>{{ prompt }}</span>
              </button>
            </div> -->
          </div>
        </div>

        <!-- SAC Display Section -->
        <div v-if="currentChat && currentChat.sacData" class="scc-sac-section">
          <div class="scc-sac-card">
            <div class="scc-sac-header" @click="toggleSacExpanded">
              <div class="scc-sac-header-left">
                <div class="scc-sac-header-icon">
                  <v-icon color="white" size="20">mdi-shield-check</v-icon>
                </div>
                <span class="scc-sac-header-title">Security Assurance Case</span>
              </div>
              <v-icon color="white" size="20">{{
                sacExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
              }}</v-icon>
            </div>

            <v-expand-transition>
              <div v-show="sacExpanded" class="scc-sac-body">
                <!-- Claims Section -->
                <div v-if="currentChat.sacData.claims && currentChat.sacData.claims.length > 0">
                  <v-expansion-panels flat accordion>
                    <v-expansion-panel
                      v-for="(claim, claimIndex) in currentChat.sacData.claims"
                      :key="`claim-${claimIndex}`"
                      class="scc-claim-panel"
                    >
                      <v-expansion-panel-header class="scc-claim-header">
                        <div class="scc-claim-header-row">
                          <div class="scc-claim-badge">{{ claimIndex + 1 }}</div>
                          <div class="scc-claim-info">
                            <div class="scc-claim-label">Claim {{ claimIndex + 1 }}</div>
                            <div class="scc-claim-preview">{{ claim.claim }}</div>
                          </div>
                          <button
                            class="scc-claim-save-btn"
                            @click.stop="saveClaim(claim, claimIndex)"
                            :disabled="savingClaims[claimIndex]"
                          >
                            <v-icon small class="mr-1">mdi-content-save-outline</v-icon>
                            {{ savingClaims[claimIndex] ? 'Saving...' : 'Save' }}
                          </button>
                        </div>
                      </v-expansion-panel-header>

                      <v-expansion-panel-content class="scc-claim-content">
                        <div class="scc-claim-text">{{ claim.claim }}</div>

                        <!-- Subclaims -->
                        <div v-if="claim.subclaims && claim.subclaims.length > 0" class="mt-3">
                          <div class="scc-section-label">
                            <v-icon small color="#7b1fa2" class="mr-1">mdi-file-tree</v-icon>
                            Sub-Claims
                          </div>
                          <subclaim-tree :subclaims="claim.subclaims" :level="0"></subclaim-tree>
                        </div>

                        <!-- Arguments (legacy) -->
                        <div v-if="claim.arguments && claim.arguments.length > 0" class="mt-3">
                          <div class="scc-section-label">
                            <v-icon small color="#7b1fa2" class="mr-1">mdi-file-tree</v-icon>
                            Arguments
                          </div>
                          <div
                            v-for="(argument, argIndex) in claim.arguments"
                            :key="`arg-${claimIndex}-${argIndex}`"
                            class="scc-argument-card"
                          >
                            <v-icon x-small color="#7b1fa2" class="mr-2 mt-1"
                              >mdi-arrow-right</v-icon
                            >
                            <div>
                              <div class="scc-argument-text">{{ argument.argument }}</div>
                              <!-- Evidence for argument -->
                              <div
                                v-if="argument.evidence && argument.evidence.length > 0"
                                class="scc-evidence-box mt-2"
                              >
                                <div class="scc-evidence-label">
                                  <v-icon x-small color="#388e3c" class="mr-1"
                                    >mdi-file-document</v-icon
                                  >
                                  Evidence
                                </div>
                                <div class="scc-evidence-chips">
                                  <div
                                    v-for="(evidence, evIndex) in argument.evidence"
                                    :key="`ev-${claimIndex}-${argIndex}-${evIndex}`"
                                    class="scc-evidence-item"
                                  >
                                    <v-chip
                                      small
                                      class="scc-ev-chip"
                                      :color="getEvidenceTypeColor(evidence.type)"
                                      outlined
                                    >
                                      <v-icon x-small left>{{
                                        getEvidenceIcon(evidence.type)
                                      }}</v-icon>
                                      <span class="font-weight-bold mr-1">{{ evidence.id }}</span>
                                      {{ evidence.title }}
                                    </v-chip>
                                    <v-chip
                                      x-small
                                      :color="getEvidenceTypeColor(evidence.type)"
                                      dark
                                      class="scc-ev-type-badge"
                                      >{{ evidence.type }}</v-chip
                                    >
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Evidence at claim level -->
                        <div
                          v-if="
                            claim.evidence &&
                              claim.evidence.length > 0 &&
                              (!claim.subclaims || claim.subclaims.length === 0) &&
                              (!claim.arguments || claim.arguments.length === 0)
                          "
                          class="scc-evidence-box mt-3"
                        >
                          <div class="scc-evidence-label">
                            <v-icon x-small color="#388e3c" class="mr-1">mdi-file-document</v-icon>
                            Evidence
                          </div>
                          <div class="scc-evidence-chips">
                            <div
                              v-for="(evidence, evIndex) in claim.evidence"
                              :key="`ev-claim-${claimIndex}-${evIndex}`"
                              class="scc-evidence-item"
                            >
                              <v-chip
                                small
                                class="scc-ev-chip"
                                :color="getEvidenceTypeColor(evidence.type)"
                                outlined
                              >
                                <v-icon x-small left>{{ getEvidenceIcon(evidence.type) }}</v-icon>
                                <span class="font-weight-bold mr-1">{{ evidence.id }}</span>
                                {{ evidence.title }}
                              </v-chip>
                              <v-chip
                                x-small
                                :color="getEvidenceTypeColor(evidence.type)"
                                dark
                                class="scc-ev-type-badge"
                                >{{ evidence.type }}</v-chip
                              >
                            </div>
                          </div>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>

                <div v-else class="text-center py-4" style="color: #90a4ae;">
                  No Security Assurance Case data available
                </div>

                <!-- SAC Action Buttons -->
                <div class="scc-sac-actions">
                  <button
                    class="scc-sac-btn scc-sac-btn--save"
                    @click="saveAllClaims"
                    :disabled="
                      savingAllClaims ||
                        !currentChat.requirementData ||
                        !currentChat.requirementData.id
                    "
                  >
                    <v-icon small class="mr-1">mdi-content-save-all-outline</v-icon>
                    {{ savingAllClaims ? 'Saving...' : 'Save All to Requirement' }}
                  </button>
                  <div class="d-flex" style="gap: 8px;">
                    <button
                      class="scc-sac-btn scc-sac-btn--outline"
                      @click="copyToClipboard(currentChat.sacData)"
                    >
                      <v-icon small class="mr-1">mdi-content-copy</v-icon>
                      Copy JSON
                    </button>
                    <button
                      class="scc-sac-btn scc-sac-btn--outline"
                      @click="downloadSacJson(currentChat.sacData)"
                    >
                      <v-icon small class="mr-1">mdi-download</v-icon>
                      Download
                    </button>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="scc-input-area">
        <div class="scc-input-card">
          <v-textarea
            v-model="userInput"
            hide-details
            rows="1"
            auto-grow
            flat
            solo
            placeholder="Message SAC Composer..."
            class="scc-input-field"
            @keydown.enter.prevent="handleEnterKey"
          ></v-textarea>
          <button
            class="scc-send-btn"
            :class="{ 'scc-send-btn--active': userInput.trim() && !isSending }"
            :disabled="!userInput.trim() || isSending"
            @click="sendMessage()"
          >
            <v-icon size="20">{{ isSending ? 'mdi-loading mdi-spin' : 'mdi-arrow-up' }}</v-icon>
          </button>
        </div>
        <div class="scc-input-hint">
          SAC Composer can make mistakes. Verify important information.
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import securityAssuranceCaseApi from '../api/securityAssuranceCaseApi';
import EventBus from '@/helpers/event-bus.js';
import { mapGetters } from 'vuex';
import SubclaimTree from '../components/SubclaimTree.vue';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';

export default {
  name: 'SacComposerChat',
  components: {
    SubclaimTree,
    Breadcrumbs,
  },
  data() {
    return {
      chats: [],
      currentChatIndex: 0,
      userInput: '',
      isSending: false,
      sidebarCollapsed: false,
      userName: localStorage.userfullname || 'User',
      loadingThreads: false,
      requirementData: null,
      socData: null,
      // examplePrompts: [
      //   'Help me create a security assurance case',
      //   'What requirements do I need for my device?',
      //   'How do I improve my evidence for compliance?',
      //   'Explain threat modeling for connected devices',
      // ],
      sacExpanded: true,
      savingClaims: {},
      savingAllClaims: false,
      // HITL state
      showHitlRejectInput: false,
      hitlExplanation: '',
      hitlPendingDecisions: [],
    };
  },
  computed: {
    ...mapGetters(['avatar']),
    currentChat() {
      return this.chats[this.currentChatIndex] || null;
    },
    showCreateSacHint() {
      const chat = this.currentChat;
      if (!chat || this.isSending) return false;
      const messages = chat.messages || [];
      const hasSac = !!(chat.sacData && chat.sacData.claims && chat.sacData.claims.length > 0);
      if (hasSac) return false;
      const lastMsg = messages[messages.length - 1];
      if (!lastMsg || lastMsg.role !== 'assistant' || lastMsg.thinking) return false;
      // Only show after the first assistant reply
      const assistantCount = messages.filter(m => m.role === 'assistant' && !m.thinking).length;
      return assistantCount >= 1;
    },

    userInitials() {
      if (!this.userName) return 'U';
      return this.userName
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase();
    },
  },
  async created() {
    // Load requirement and SOC context from localStorage (set by RequirementsList)
    try {
      const chatData = JSON.parse(localStorage.getItem('chatData') || '{}');
      if (chatData.requirement) {
        this.requirementData = chatData.requirement;
      }
      if (chatData.soc) {
        this.socData = chatData.soc;
      }
    } catch (e) {
      console.error('Error parsing chatData:', e);
    }

    // Load existing threads from SAC Composer
    await this.loadUserThreads();

    // If navigated with a requirement, create a new chat and auto-send first message
    if (this.requirementData) {
      const threadId = this.generateThreadId();
      const reqTitle = this.requirementData.title || '';
      // const chatTitle =
      //   reqTitle.length > 30 ? reqTitle.substring(0, 30) + '...' : reqTitle || 'New chat';
      const chatTitle = reqTitle || 'New chat';
      this.chats.unshift({
        title: chatTitle,
        threadId,
        createdAt: new Date().toISOString(),
        messages: [],
        requirementData: this.requirementData,
        socData: this.socData,
        sacData: null,
      });
      this.currentChatIndex = 0;

      // Auto-send the first message to create the SAC Composer session
      const reqCodePart = this.requirementData.requirementId
        ? ` [${this.requirementData.requirementId}]`
        : '';
      const initialMessage = `I need help creating a Security Assurance Case for requirement "${this.requirementData.title}"${reqCodePart} (ID: ${this.requirementData.id}). This requirement is from the ${this.requirementData.family} standard. Please guide me through creating claims, arguments, and evidence.`;
      await this.sendMessage(initialMessage);

      // Clear localStorage so refreshing doesn't re-trigger
      localStorage.removeItem('chatData');
    } else {
      // No requirement context — ensure there's at least one empty chat
      if (this.chats.length === 0) {
        this.chats.push({
          title: 'New chat',
          threadId: this.generateThreadId(),
          createdAt: new Date().toISOString(),
          messages: [],
          requirementData: null,
          sacData: null,
        });
      }
      this.currentChatIndex = 0;
    }
  },
  methods: {
    // ── Thread ID & Date helpers ────────────────────────────────────
    generateThreadId() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const now = new Date();
      const diff = now - d;
      if (diff < 86400000) return 'Today';
      if (diff < 172800000) return 'Yesterday';
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },

    // ── Load User Threads ───────────────────────────────────────────
    async loadUserThreads() {
      this.loadingThreads = true;
      try {
        const response = await securityAssuranceCaseApi.getUserThreads();
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

        // New API returns { threads: [{thread_id, created_at}], total, ... }
        // Old API returned { thread_ids: [...] }
        const threads = data.threads || data.thread_ids || [];

        if (Array.isArray(threads) && threads.length > 0) {
          for (const thread of threads) {
            const rawId =
              typeof thread === 'string' ? thread : thread.thread_id || thread.id || null;
            const createdAt = typeof thread === 'object' ? thread.created_at || null : null;
            if (!rawId) continue;

            // LangGraph stores threads as "user_id:thread_id" — extract just the thread part
            const threadId = rawId.includes(':') ? rawId.split(':').pop() : rawId;
            if (!threadId) continue;

            await this.loadThreadState(threadId, createdAt);
          }
        }
      } catch (error) {
        console.error('Error loading user threads:', error);
      } finally {
        this.loadingThreads = false;
      }
    },

    async loadThreadState(threadId, createdAt) {
      try {
        const response = await securityAssuranceCaseApi.getThreadState(threadId);
        const state = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

        // Extract messages — handle both flat state and array-wrapped state
        const stateObj = Array.isArray(state) ? state[0] : state;
        const rawMessages = stateObj?.messages || [];
        const convertedMessages = [];

        if (Array.isArray(rawMessages)) {
          rawMessages.forEach(msg => {
            const role = msg.type === 'human' ? 'user' : 'assistant';
            let content = msg.content || '';

            // Content can be an array for tool-call messages — flatten to text
            if (Array.isArray(content)) {
              content = content
                .filter(c => c.type === 'text')
                .map(c => c.text || '')
                .join('\n');
            }

            // Handle structured format: content='...'
            if (typeof content === 'string' && content.includes("content='")) {
              const match = content.match(/content='([^']*)'/);
              if (match) content = match[1];
            }

            // Skip empty internal tool messages
            if (!content && msg.type === 'tool') return;

            convertedMessages.push({ role, content });
          });
        }

        // Derive chat title
        let chatTitle = 'Chat ' + threadId.substring(0, 8);
        let requirementData = null;
        const firstHuman = convertedMessages.find(m => m.role === 'user');

        // Try to get requirement info from state or message content
        const reqId = stateObj?.requirement_id;
        if (reqId) {
          requirementData = { id: reqId };
          if (firstHuman) {
            const titleMatch = firstHuman.content.match(/requirement "([^"]+)"/);
            const familyMatch = firstHuman.content.match(/from the ([^.]+) standard/);
            if (titleMatch) {
              requirementData.title = titleMatch[1];
              chatTitle = titleMatch[1];
            }
            if (familyMatch) requirementData.family = familyMatch[1];
          }
          // } else if (firstHuman) {
          //   chatTitle =
          //     firstHuman.content.length > 30
          //       ? firstHuman.content.substring(0, 30) + '...'
          //       : firstHuman.content;
          // }
        } else if (firstHuman) {
          chatTitle = firstHuman.content;
        }

        // Extract SAC data — first from state, then from dedicated endpoint
        let sacData = stateObj?.sac ? this.parseSacData(stateObj.sac) : null;
        if (!sacData) {
          try {
            const sacResp = await securityAssuranceCaseApi.getSessionSac(threadId);
            const sacRaw =
              typeof sacResp.data === 'string' ? JSON.parse(sacResp.data) : sacResp.data;
            // Response is { sac: { ... } } — unwrap the envelope
            const sacPayload = sacRaw?.sac ?? sacRaw;
            if (sacPayload) sacData = this.parseSacData(sacPayload);
          } catch (sacErr) {
            // SAC not yet available for this thread
          }
        }

        // Upsert chat
        const existingIdx = this.chats.findIndex(c => c.threadId === threadId);
        const chatObj = {
          title: chatTitle,
          threadId,
          createdAt: createdAt || new Date().toISOString(),
          messages: convertedMessages,
          sacData,
          requirementData,
        };

        if (existingIdx !== -1) {
          this.$set(this.chats, existingIdx, { ...this.chats[existingIdx], ...chatObj });
        } else {
          this.chats.push(chatObj);
        }
      } catch (error) {
        console.error(`Error loading thread state for ${threadId}:`, error);
      }
    },

    parseSacData(sacContent) {
      if (!sacContent) return null;
      try {
        const raw = typeof sacContent === 'string' ? JSON.parse(sacContent) : sacContent;
        return this.normalizeSacData(raw);
      } catch (error) {
        console.error('Error parsing SAC data:', error);
        return null;
      }
    },

    // Normalize the SAC Composer API format into the internal format the template expects.
    // API returns a single top-level claim object: { id, title, text, subclaims[], evidences[] }
    // Template expects: { claims: [{ claim, id, subclaims[], evidence[] }] }
    normalizeSacData(raw) {
      if (!raw) return null;

      // Already normalized (has claims array)
      if (raw.claims && Array.isArray(raw.claims)) return raw;

      // Single top-level claim object from SAC Composer
      const normalizeSubclaim = sc => ({
        claim: sc.text || sc.title || '',
        id: sc.id || '',
        subclaims: (sc.subclaims || []).map(normalizeSubclaim),
        evidence: sc.evidences || sc.evidence || [],
      });

      return {
        claims: [
          {
            claim: raw.text || raw.title || '',
            id: raw.id || '',
            subclaims: (raw.subclaims || []).map(normalizeSubclaim),
            evidence: raw.evidences || raw.evidence || [],
          },
        ],
      };
    },

    // ── Chat Management ─────────────────────────────────────────────
    createNewChat() {
      const emptyIdx = this.chats.findIndex(c => c.messages.length === 0);
      if (emptyIdx !== -1) {
        this.currentChatIndex = emptyIdx;
        this.userInput = '';
        return;
      }

      this.chats.unshift({
        title: 'New chat',
        threadId: this.generateThreadId(),
        createdAt: new Date().toISOString(),
        messages: [],
        requirementData: null,
        sacData: null,
      });
      this.currentChatIndex = 0;
      this.userInput = '';
    },

    async selectChat(index) {
      this.currentChatIndex = index;
      this.requirementData = this.chats[index]?.requirementData || null;
      this.showHitlRejectInput = false;
      this.hitlExplanation = '';

      // Reload messages and SAC from server so history is always up-to-date
      const chat = this.chats[index];
      if (chat && chat.threadId) {
        await this.loadThreadState(chat.threadId, chat.createdAt);
        // Re-sync index in case chats array was reordered by upsert
        const updatedIdx = this.chats.findIndex(c => c.threadId === chat.threadId);
        if (updatedIdx !== -1) this.currentChatIndex = updatedIdx;
      }
    },

    async deleteChat(index) {
      const chat = this.chats[index];
      try {
        if (chat.threadId && chat.messages.length > 0) {
          await securityAssuranceCaseApi.deleteThread(chat.threadId);
        }
      } catch (error) {
        console.error('Error deleting thread:', error);
      }

      if (this.chats.length === 1) {
        this.chats = [
          {
            title: 'New chat',
            threadId: this.generateThreadId(),
            createdAt: new Date().toISOString(),
            messages: [],
            requirementData: null,
            sacData: null,
          },
        ];
        this.currentChatIndex = 0;
      } else {
        this.chats.splice(index, 1);
        if (this.currentChatIndex >= this.chats.length) {
          this.currentChatIndex = this.chats.length - 1;
        }
      }
    },

    handleEnterKey(event) {
      if (event.ctrlKey || event.shiftKey) return;
      this.sendMessage();
    },

    // ── Send Message ────────────────────────────────────────────────
    async sendMessage(customMessage = null) {
      const messageContent = customMessage || this.userInput;
      if (!messageContent.trim() || this.isSending) return;

      const chat = this.currentChat;
      if (!chat) return;

      // Add user message to the conversation
      chat.messages.push({ role: 'user', content: messageContent });
      if (!customMessage) this.userInput = '';

      // Set title from first user message
      // if (chat.messages.filter(m => m.role === 'user').length === 1) {
      //   chat.title =
      //     messageContent.length > 30 ? messageContent.substring(0, 30) + '...' : messageContent;
      // }
      if (chat.messages.filter(m => m.role === 'user').length === 1) {
        chat.title = messageContent;
      }

      this.scrollToBottom();

      // Add thinking indicator
      chat.messages.push({ role: 'assistant', content: '', thinking: true });
      this.isSending = true;

      try {
        // Build API options
        const options = {};
        const reqData = chat.requirementData;
        if (reqData && reqData.family) {
          options.standardName = this.mapFamilyToStandard(reqData.family);
        }

        // Include subject of certification context on the first message only
        const isFirstMessage = !chat.messages.some(m => m.role === 'assistant' && !m.thinking);
        if (isFirstMessage && chat.socData) {
          options.contextOfCertification = this.buildContextOfCertification(chat.socData);
        }

        // Call the SAC Composer chat endpoint
        const response = await securityAssuranceCaseApi.chatWithSacComposer(
          chat.threadId,
          messageContent,
          options,
        );

        // Remove thinking indicator
        chat.messages.pop();

        // Parse response
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        const aiText = this.extractAiResponse(data);
        const aiMessage = { role: 'assistant', content: aiText };

        // Check for HITL interrupt
        const interrupt =
          data?.__interrupt || data?.interrupted_actions || data?.pending_tool_calls;
        if (interrupt && Array.isArray(interrupt) && interrupt.length > 0) {
          aiMessage.hitlPending = true;
          this.hitlPendingDecisions = interrupt;
        }

        chat.messages.push(aiMessage);

        // Extract SAC data if present in response
        const sacContent = data?.sac || data?.state?.sac;
        if (sacContent) {
          const sacData = this.parseSacData(sacContent);
          if (sacData) this.$set(chat, 'sacData', sacData);
        }

        // Always fetch latest SAC from the dedicated endpoint
        try {
          const sacResp = await securityAssuranceCaseApi.getSessionSac(chat.threadId);
          const sacRaw = typeof sacResp.data === 'string' ? JSON.parse(sacResp.data) : sacResp.data;
          // Response is { sac: { ... } } — unwrap the envelope
          const sacPayload = sacRaw?.sac ?? sacRaw;
          if (sacPayload) {
            const sacData = this.parseSacData(sacPayload);
            if (sacData) this.$set(chat, 'sacData', sacData);
          }
        } catch (sacErr) {
          console.debug('SAC not yet available:', sacErr);
        }
      } catch (error) {
        // Remove thinking indicator on error
        chat.messages.pop();

        let errorMsg = "I'm sorry, I encountered an error processing your request.";
        if (error.response) {
          const status = error.response.status;
          if (status === 504 || status === 408) {
            errorMsg =
              '⏱️ The request timed out. Try simplifying your question or try again in a moment.';
          } else if (status === 502 || status === 503) {
            errorMsg = '🔌 The AI service is temporarily unavailable. Please try again shortly.';
          } else if (error.response.data) {
            const d = error.response.data;
            if (d.detail)
              errorMsg +=
                ' ' + (typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail));
            else if (d.error) errorMsg = d.error;
          }
        } else if (error.message && error.message.includes('timeout')) {
          errorMsg = '⏱️ Request timeout. Please try a simpler question.';
        }

        chat.messages.push({ role: 'assistant', content: errorMsg });
        EventBus.$emit('snackbar', {
          text: 'Error communicating with AI service.',
          color: 'error',
        });
      } finally {
        this.isSending = false;
        this.scrollToBottom();
      }
    },

    // ── HITL (Human-in-the-Loop) ────────────────────────────────────
    async handleHitlAction(type) {
      const chat = this.currentChat;
      if (!chat) return;

      // Build decisions
      const decisions =
        this.hitlPendingDecisions.length > 0
          ? this.hitlPendingDecisions.map(() => {
              const d = { type };
              if (type === 'reject' && this.hitlExplanation.trim()) {
                d.explanation = this.hitlExplanation.trim();
              }
              return d;
            })
          : [
              {
                type,
                ...(type === 'reject' && this.hitlExplanation.trim()
                  ? { explanation: this.hitlExplanation.trim() }
                  : {}),
              },
            ];

      // Clear HITL state
      const lastMsg = chat.messages[chat.messages.length - 1];
      if (lastMsg) lastMsg.hitlPending = false;
      this.showHitlRejectInput = false;
      this.hitlExplanation = '';
      this.hitlPendingDecisions = [];

      // Show user decision in conversation
      chat.messages.push({
        role: 'user',
        content:
          type === 'approve'
            ? '✅ Approved the proposed action.'
            : '❌ Rejected: ' + (decisions[0].explanation || 'No reason provided.'),
      });

      // Thinking indicator
      chat.messages.push({ role: 'assistant', content: '', thinking: true });
      this.isSending = true;
      this.scrollToBottom();

      try {
        const standardName = chat.requirementData?.family
          ? this.mapFamilyToStandard(chat.requirementData.family)
          : null;

        const response = await securityAssuranceCaseApi.hitlConfirm(
          chat.threadId,
          decisions,
          standardName,
        );

        chat.messages.pop();

        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        const aiText = this.extractAiResponse(data);
        const aiMessage = { role: 'assistant', content: aiText };

        // Check for another HITL interrupt
        const interrupt =
          data?.__interrupt || data?.interrupted_actions || data?.pending_tool_calls;
        if (interrupt && Array.isArray(interrupt) && interrupt.length > 0) {
          aiMessage.hitlPending = true;
          this.hitlPendingDecisions = interrupt;
        }

        chat.messages.push(aiMessage);

        // Update SAC data from response
        const sacContent = data?.sac || data?.state?.sac;
        if (sacContent) {
          const sacData = this.parseSacData(sacContent);
          if (sacData) this.$set(chat, 'sacData', sacData);
        }

        // Always fetch latest SAC from the dedicated endpoint
        try {
          const sacResp = await securityAssuranceCaseApi.getSessionSac(chat.threadId);
          const sacRaw = typeof sacResp.data === 'string' ? JSON.parse(sacResp.data) : sacResp.data;
          // Response is { sac: { ... } } — unwrap the envelope
          const sacPayload = sacRaw?.sac ?? sacRaw;
          if (sacPayload) {
            const sacData = this.parseSacData(sacPayload);
            if (sacData) this.$set(chat, 'sacData', sacData);
          }
        } catch (sacErr) {
          console.debug('SAC not yet available:', sacErr);
        }
      } catch (error) {
        chat.messages.pop();
        chat.messages.push({
          role: 'assistant',
          content: 'Error processing your decision. Please try again.',
        });
        console.error('HITL confirm error:', error);
      } finally {
        this.isSending = false;
        this.scrollToBottom();
      }
    },

    // ── Response Parsing ────────────────────────────────────────────
    extractAiResponse(data) {
      if (!data) return 'No response received.';

      // Handle messages array (common SAC Composer response format)
      if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
        // Find the last AI/assistant message
        const lastAi = [...data.messages]
          .reverse()
          .find(
            m =>
              m.type === 'ai' ||
              m.role === 'assistant' ||
              (typeof m === 'object' && m.content && m.type !== 'human'),
          );

        if (lastAi) {
          let content = lastAi.content || '';

          // Handle structured format: content='...'
          if (typeof content === 'string' && content.includes("content='")) {
            const match = content.match(/content='([\s\S]*?)(?:'(?:\s|$))/);
            if (match) content = match[1];
          }
          return content || JSON.stringify(lastAi);
        }

        // Fallback to last message
        const last = data.messages[data.messages.length - 1];
        return typeof last === 'string' ? last : last?.content || JSON.stringify(last);
      }

      // Other response formats
      if (typeof data === 'string') return data;
      if (data.response) return data.response;
      if (data.message && typeof data.message === 'string') return data.message;
      if (data.content) return data.content;
      if (data.output) return data.output;

      return JSON.stringify(data, null, 2);
    },

    buildContextOfCertification(soc) {
      if (!soc) return null;
      const standardId = this.mapStandardFamilyToIdentifier(soc.standardFamily);
      const subjectType = this.mapStandardFamilyToSubjectType(soc.standardFamily);

      const subjectForCertification = {
        name: soc.name,
        // `identification` is required by the SAC Composer API
        identification: soc.versionLabel || soc.name,
        subject_type: subjectType,
        standards_that_apply: standardId ? [standardId] : [],
      };

      if (soc.description) {
        subjectForCertification.description = soc.description;
      }

      if (subjectType === 'process') {
        subjectForCertification.process = { name: soc.name };
        subjectForCertification.product = null;
      } else {
        subjectForCertification.product = { name: soc.name };
        subjectForCertification.process = null;
      }

      return {
        name: soc.name,
        responsible_entity: {
          name: this.userName,
          role: 'product supplier',
        },
        subject_for_certification: subjectForCertification,
      };
    },

    mapStandardFamilyToIdentifier(family) {
      if (!family) return null;
      const f = family.toLowerCase();
      if (f.includes('62443') && f.includes('4-1')) return 'IEC_62443_4_1';
      if (f.includes('62443') && f.includes('4-2')) return 'IEC_62443_4_2';
      if (f.includes('62443') && f.includes('2-4')) return 'IEC_62443_2_4';
      if (f.includes('cra') || f.includes('resilience')) return 'CRA';
      return null;
    },

    mapStandardFamilyToSubjectType(family) {
      if (!family) return 'product';
      const f = family.toLowerCase();
      // IEC 62443-2-4 is about service provider aspects (process-oriented)
      if (f.includes('62443') && f.includes('2-4')) return 'process';
      // All other standards default to product
      return 'product';
    },

    mapFamilyToStandard(family) {
      if (!family) return null;
      const f = family.toLowerCase();
      if (f.includes('62443') && f.includes('4-1'))
        return 'IEC 62443-4-1: Secure product development lifecycle model';
      if (f.includes('62443') && f.includes('4-2'))
        return 'IEC 62443-4-2: Security requirements for IACS components';
      if (f.includes('62443') && f.includes('2-4'))
        return 'IEC 62443-2-4: Security management system for asset owners';
      if (f.includes('cra') || f.includes('resilience')) return 'Cybersecurity Resilience Act';
      return null;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
        }
      });
    },

    formatMessage(text) {
      // Basic markdown-like formatting
      if (!text) return '';

      // Format JSON objects if detected
      text = this.formatJsonInText(text);

      // Handle tables - convert markdown tables to HTML tables with styling
      text = this.formatTables(text);

      // Format headers
      text = text.replace(/### (.*?)$/gm, '<h3 class="ai-header">$1</h3>');
      text = text.replace(/## (.*?)$/gm, '<h2 class="ai-header">$1</h2>');
      text = text.replace(/# (.*?)$/gm, '<h1 class="ai-header">$1</h1>');

      // Format bold text
      text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Format italic text
      text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');

      // Format lists - fixed escape characters
      text = text.replace(/^\s*[-*]\s+(.*?)$/gm, '<li class="ai-list-item">$1</li>');
      text = text.replace(
        /(<li class="ai-list-item">.*?<\/li>)(?:\s*[\r\n]+\s*<li class="ai-list-item">)/gs,
        '$1',
      );
      text = text.replace(/(<li class="ai-list-item">.*?<\/li>)+/g, '<ul class="ai-list">$&</ul>');

      // Format numbered lists - fixed escape characters
      text = text.replace(/^\s*(\d+)[.)]\s+(.*?)$/gm, '<li class="ai-list-item numbered">$2</li>');
      text = text.replace(
        /(<li class="ai-list-item numbered">.*?<\/li>)(?:\s*[\r\n]+\s*<li class="ai-list-item numbered">)/gs,
        '$1',
      );
      text = text.replace(
        /(<li class="ai-list-item numbered">.*?<\/li>)+/g,
        '<ol class="ai-list numbered">$&</ol>',
      );

      // Format blockquotes - fixed escape characters
      text = text.replace(/^>\s+(.*?)$/gm, '<blockquote class="ai-blockquote">$1</blockquote>');

      // Format horizontal rules
      text = text.replace(/^---$/gm, '<hr class="ai-hr">');

      // Convert code blocks (must come after JSON formatting)
      text = text.replace(/```([\s\S]*?)```/g, '<pre class="code-block">$1</pre>');

      // Convert inline code (must come after JSON formatting)
      text = text.replace(/`([^`]+)`/g, '<code>$1</code>');

      // Convert line breaks to <br> (do this last to avoid interfering with other formatting)
      text = text.replace(/\n/g, '<br>');

      return text;
    },

    formatJsonInText(text) {
      // Look for JSON objects in text and format them
      try {
        // Find JSON-like structures in the text
        const jsonPattern = /\{[\s\S]*?\}(?=\n|$)/g;
        return text.replace(jsonPattern, match => {
          try {
            // Try to parse and pretty-print the JSON
            const jsonObj = JSON.parse(match);
            const prettyJson = JSON.stringify(jsonObj, null, 2);
            return `<pre class="json-block"><code>${this.syntaxHighlightJson(
              prettyJson,
            )}</code></pre>`;
          } catch (e) {
            // If it's not valid JSON, return it unchanged
            return match;
          }
        });
      } catch (e) {
        return text;
      }
    },

    syntaxHighlightJson(json) {
      // Add syntax highlighting to JSON
      if (!json) return '';

      json = json
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      // Fixed escape character in regex
      return json.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        match => {
          let cls = 'json-number';
          if (/^"/.test(match)) {
            if (/:$/.test(match)) {
              cls = 'json-key';
            } else {
              cls = 'json-string';
            }
          } else if (/true|false/.test(match)) {
            cls = 'json-boolean';
          } else if (/null/.test(match)) {
            cls = 'json-null';
          }
          return `<span class="${cls}">${match}</span>`;
        },
      );
    },

    formatTables(text) {
      // Look for markdown tables and convert them to HTML tables
      const tableRegex = /\|(.+)\|\n\|(?:-+\|)+\n((?:\|.+\|\n)+)/g;

      return text.replace(tableRegex, (match, headerRow, bodyRows) => {
        // Process the header
        const headers = headerRow
          .split('|')
          .map(h => h.trim())
          .filter(h => h !== '');

        // Process the body rows
        const bodyRowsArray = bodyRows.trim().split('\n');
        const rows = bodyRowsArray.map(row => {
          const cells = row
            .split('|')
            .map(cell => cell.trim())
            .filter(cell => cell !== '');
          return cells;
        });

        // Build the HTML table
        let table = '<div class="table-container"><table class="ai-table">';

        // Add the header
        table += '<thead><tr>';
        headers.forEach(header => {
          table += `<th>${header}</th>`;
        });
        table += '</tr></thead>';

        // Add the body
        table += '<tbody>';
        rows.forEach(row => {
          table += '<tr>';
          row.forEach(cell => {
            // Apply status-based styling
            let cellClass = '';
            if (cell.includes('✅')) {
              cellClass = ' class="status-success"';
            } else if (cell.includes('⚠️')) {
              cellClass = ' class="status-warning"';
            } else if (cell.includes('❌')) {
              cellClass = ' class="status-error"';
            }
            table += `<td${cellClass}>${cell}</td>`;
          });
          table += '</tr>';
        });
        table += '</tbody></table></div>';

        return table;
      });
    },

    toggleSacExpanded() {
      this.sacExpanded = !this.sacExpanded;
    },

    getEvidenceTypeColor(type) {
      const colorMap = {
        Document: 'blue',
        Diagram: 'purple',
        'Test Report': 'green',
        'Audit Report': 'orange',
        Report: 'orange',
        'Review Report': 'indigo',
        Data: 'teal',
        'Log Data': 'cyan',
        'Contractual Document': 'deep-purple',
        'Policy Document': 'pink',
        // Source-based colors
        'Threat Spider': 'purple',
        'Dependency Track': 'orange',
        'Threat Dragon JSON': 'teal',
      };
      return colorMap[type] || 'grey';
    },

    getEvidenceIcon(type) {
      const iconMap = {
        Document: 'mdi-file-document',
        Diagram: 'mdi-chart-box',
        'Test Report': 'mdi-test-tube',
        'Audit Report': 'mdi-clipboard-check',
        Report: 'mdi-clipboard-text',
        'Review Report': 'mdi-file-search',
        Data: 'mdi-database',
        'Log Data': 'mdi-math-log',
        'Contractual Document': 'mdi-file-sign',
        'Policy Document': 'mdi-shield-account',
        // Source-based icons
        'Threat Spider': 'mdi-spider',
        'Dependency Track': 'mdi-package-variant',
        'Threat Dragon JSON': 'mdi-dragon',
      };
      return iconMap[type] || 'mdi-file';
    },

    copyToClipboard(sacData) {
      const jsonString = JSON.stringify(sacData, null, 2);

      const fallback = () => {
        const textarea = document.createElement('textarea');
        textarea.value = jsonString;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        EventBus.$emit('snackbar', {
          text: 'Security Assurance Case JSON copied to clipboard',
          color: 'success',
        });
      };

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(jsonString)
          .then(() => {
            EventBus.$emit('snackbar', {
              text: 'Security Assurance Case JSON copied to clipboard',
              color: 'success',
            });
          })
          .catch(() => fallback());
      } else {
        fallback();
      }
    },

    downloadSacJson(sacData) {
      const jsonString = JSON.stringify(sacData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sac-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      EventBus.$emit('snackbar', {
        text: 'Security Assurance Case JSON downloaded',
        color: 'success',
      });
    },

    async saveClaim(claim, claimIndex) {
      // Set loading state for this specific claim
      this.$set(this.savingClaims, claimIndex, true);

      try {
        // Get requirement data from current chat, with fallback to parse from first user message
        let requirementData = this.currentChat.requirementData;

        if (!requirementData || !requirementData.id) {
          const firstHuman = (this.currentChat.messages || []).find(m => m.role === 'user');
          if (firstHuman) {
            const idMatch = firstHuman.content.match(/\(ID:\s*([^)]+)\)/);
            const titleMatch = firstHuman.content.match(/requirement "([^"]+)"/);
            const familyMatch = firstHuman.content.match(/from the ([^.]+) standard/);
            if (idMatch) {
              requirementData = {
                id: idMatch[1].trim(),
                title: titleMatch ? titleMatch[1] : idMatch[1].trim(),
                family: familyMatch ? familyMatch[1] : null,
              };
              this.$set(this.currentChat, 'requirementData', requirementData);
            }
          }
        }

        if (!requirementData || !requirementData.id) {
          throw new Error('Requirement data is not available. Cannot save claim.');
        }

        console.log('Saving claim with requirement data:', {
          claim,
          claimIndex,
          requirementData,
          threadId: this.currentChat.threadId,
        });

        // Prepare claim data in the format expected by the backend
        const claimToSave = {
          description: claim.claim || claim.description,
          subClaims: [],
        };

        // Process subclaims if they exist
        if (claim.subclaims && claim.subclaims.length > 0) {
          claimToSave.subClaims = claim.subclaims.map(subclaim => ({
            description: subclaim.claim || subclaim.subclaim || subclaim.description,
            evidences:
              subclaim.evidence && subclaim.evidence.length > 0
                ? subclaim.evidence.map(ev => ({
                    title: ev.title || ev.id,
                    description: ev.description || ev.title || '',
                    type: ev.type || 'Document',
                    fileName: ev.fileName || 'null',
                  }))
                : [],
          }));
        }
        // Handle legacy arguments structure
        else if (claim.arguments && claim.arguments.length > 0) {
          claimToSave.subClaims = claim.arguments.map(argument => ({
            description: argument.argument,
            evidences:
              argument.evidence && argument.evidence.length > 0
                ? argument.evidence.map(ev => ({
                    title: ev.title || ev.id,
                    description: ev.description || ev.title || '',
                    type: ev.type || 'Document',
                    fileName: ev.fileName || 'null',
                  }))
                : [],
          }));
        }
        // Handle evidence at claim level
        else if (claim.evidence && claim.evidence.length > 0) {
          // Create a single subclaim to hold the evidence
          claimToSave.subClaims = [
            {
              description: 'Evidence for ' + (claim.claim || claim.description).substring(0, 50),
              evidences: claim.evidence.map(ev => ({
                title: ev.title || ev.id,
                description: ev.description || ev.title || '',
                type: ev.type || 'Document',
                fileName: ev.fileName || 'null',
              })),
            },
          ];
        }

        // Call the API to save the claim
        await securityAssuranceCaseApi.saveClaim(requirementData.id, claimToSave);

        EventBus.$emit('snackbar', {
          text: `Claim ${claimIndex + 1} saved successfully to requirement "${
            requirementData.title
          }"`,
          color: 'success',
        });
      } catch (error) {
        console.error('Error saving claim:', error);
        EventBus.$emit('snackbar', {
          text:
            'Failed to save claim: ' +
            (error.response?.data?.error || error.message || 'Unknown error'),
          color: 'error',
        });
      } finally {
        // Clear loading state
        this.$set(this.savingClaims, claimIndex, false);
      }
    },

    async saveAllClaims() {
      if (!this.currentChat.sacData) {
        EventBus.$emit('snackbar', {
          text: 'No Security Assurance Case data available',
          color: 'error',
        });
        return;
      }

      // Resolve requirement data with fallback from first message
      let requirementData = this.currentChat.requirementData;
      if (!requirementData || !requirementData.id) {
        const firstHuman = (this.currentChat.messages || []).find(m => m.role === 'user');
        if (firstHuman) {
          const idMatch = firstHuman.content.match(/\(ID:\s*([^)]+)\)/);
          const titleMatch = firstHuman.content.match(/requirement "([^"]+)"/);
          const familyMatch = firstHuman.content.match(/from the ([^.]+) standard/);
          if (idMatch) {
            requirementData = {
              id: idMatch[1].trim(),
              title: titleMatch ? titleMatch[1] : idMatch[1].trim(),
              family: familyMatch ? familyMatch[1] : null,
            };
            this.$set(this.currentChat, 'requirementData', requirementData);
          }
        }
      }

      if (!requirementData || !requirementData.id) {
        EventBus.$emit('snackbar', {
          text: 'Requirement information is not available. Cannot save claims.',
          color: 'error',
        });
        return;
      }

      this.savingAllClaims = true;

      try {
        console.log('Saving all claims for requirement:', requirementData.id);

        // Prepare the SAC data in the format expected by the backend
        const sacDataToSave = {
          requirementId: requirementData.id,
          claims: this.currentChat.sacData.claims.map(claim => ({
            description: claim.claim || claim.description,
            subClaims: this.processSubClaims(claim),
          })),
        };

        console.log('Prepared SAC data:', sacDataToSave);

        // Call the API endpoint to save all claims
        // This endpoint will handle both creation and replacement
        await securityAssuranceCaseApi.saveAllClaims(sacDataToSave);

        EventBus.$emit('snackbar', {
          text: `Successfully saved ${sacDataToSave.claims.length} claim(s) to requirement "${requirementData.title}". Redirecting...`,
          color: 'success',
        });

        // Navigate to the Claims view for this requirement
        localStorage.setItem('inScopeRequirement', JSON.stringify(requirementData));
        this.$router.push({ name: 'Claims' });
      } catch (error) {
        console.error('Error saving all claims:', error);
        EventBus.$emit('snackbar', {
          text:
            'Failed to save claims: ' +
            (error.response?.data?.error || error.message || 'Unknown error'),
          color: 'error',
        });
      } finally {
        this.savingAllClaims = false;
      }
    },

    processSubClaims(claim) {
      // Process subclaims if they exist
      if (claim.subclaims && claim.subclaims.length > 0) {
        return claim.subclaims.map(subclaim => ({
          description: subclaim.claim || subclaim.subclaim || subclaim.description,
          evidences: this.processEvidences(subclaim.evidence),
        }));
      }
      // Handle legacy arguments structure
      else if (claim.arguments && claim.arguments.length > 0) {
        return claim.arguments.map(argument => ({
          description: argument.argument,
          evidences: this.processEvidences(argument.evidence),
        }));
      }
      // Handle evidence at claim level
      else if (claim.evidence && claim.evidence.length > 0) {
        return [
          {
            description: 'Evidence for ' + (claim.claim || claim.description).substring(0, 50),
            evidences: this.processEvidences(claim.evidence),
          },
        ];
      }

      return [];
    },

    processEvidences(evidenceArray) {
      if (!evidenceArray || evidenceArray.length === 0) {
        return [];
      }

      return evidenceArray.map(ev => ({
        title: ev.title || ev.id,
        description: ev.description || ev.title || '',
        type: ev.type || 'Document',
        fileName: ev.fileName || 'null',
      }));
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════
   ROOT LAYOUT
   ═══════════════════════════════════════════ */
.scc-root {
  display: flex;
  height: 88vh;
  overflow: hidden;
  border-radius: 16px;
  background: #f7f8fa;
  box-shadow: 0 4px 24px rgba(0, 64, 94, 0.08);
  margin: 4px;
}

/* ═══════════════════════════════════════════
   SIDEBAR
   ═══════════════════════════════════════════ */
.scc-sidebar {
  width: 270px;
  min-width: 270px;
  background: linear-gradient(180deg, #0a1628 0%, #122240 60%, #162a4a 100%);
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: 1px solid rgba(255, 255, 255, 0.06);
}
.scc-sidebar--collapsed {
  min-width: 0;
  width: 0;
  overflow: hidden;
}

/* Brand */
.scc-sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 16px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.scc-sidebar-brand-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, #00405e 0%, #0097a7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.scc-sidebar-brand-text {
  font-size: 1.05rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.3px;
}

/* New Chat Button */
.scc-sidebar-new {
  padding: 12px 12px 8px;
}
.scc-new-chat-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.scc-new-chat-btn:hover {
  background: rgba(0, 151, 167, 0.15);
  border-color: rgba(0, 151, 167, 0.4);
  color: #4dd0e1;
}

/* Chat List */
.scc-chat-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 8px 4px;
}
.scc-chat-list-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.3);
  padding: 8px 10px 6px;
}
.scc-chat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  margin-bottom: 2px;
}
.scc-chat-item:hover {
  background: rgba(255, 255, 255, 0.06);
}
.scc-chat-item--active {
  background: rgba(0, 151, 167, 0.12) !important;
  border-left: 3px solid #0097a7;
}
.scc-chat-item-icon {
  color: rgba(255, 255, 255, 0.35) !important;
  flex-shrink: 0;
}
.scc-chat-item--active .scc-chat-item-icon {
  color: #4dd0e1 !important;
}
.scc-chat-item-title {
  flex: 1;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.72);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.scc-chat-item--active .scc-chat-item-title {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
}
.scc-chat-item-delete {
  opacity: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(238, 6, 6, 0.959) !important;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.15s;
  flex-shrink: 0;
}
.scc-chat-item:hover .scc-chat-item-delete {
  opacity: 1;
}
.scc-chat-item-delete:hover {
  color: #f30804 !important;
  background: rgba(239, 83, 80, 0.12);
}

/* Sidebar Footer */
.scc-sidebar-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(0, 0, 0, 0.15);
}
.scc-sidebar-footer-avatar {
  border: 2px solid rgba(0, 151, 167, 0.5);
}
.scc-sidebar-footer-name {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 500;
}

/* Scrollbar */
.scc-chat-list::-webkit-scrollbar {
  width: 4px;
}
.scc-chat-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

/* ═══════════════════════════════════════════
   MAIN AREA
   ═══════════════════════════════════════════ */
.scc-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  min-width: 0;
}

/* Top Bar */
.scc-topbar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px 0;
  background: #fff;
}
.scc-topbar >>> .v-breadcrumbs {
  background: #f8fafc;
  padding: 8px 18px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.88rem;
  border: 1px solid #e8ecf1;
}

/* ═══════════════════════════════════════════
   MESSAGES
   ═══════════════════════════════════════════ */
.scc-messages {
  flex: 1;
  overflow-y: auto;
  scroll-behavior: smooth;
}
.scc-messages::-webkit-scrollbar {
  width: 6px;
}
.scc-messages::-webkit-scrollbar-thumb {
  background: #cfd8dc;
  border-radius: 6px;
}
.scc-messages-inner {
  max-width: 820px;
  margin: 0 auto;
  padding: 16px 24px;
}

/* Message Bubble */
.scc-msg {
  margin-bottom: 4px;
  animation: scc-fade-in 0.3s ease;
}
@keyframes scc-fade-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.scc-msg--user {
  border-bottom: 1px solid #f0f2f5;
}
.scc-msg--assistant {
  border-bottom: 1px solid #f5f7fa;
}
.scc-msg-row {
  display: flex;
  gap: 14px;
  padding: 18px 0;
  align-items: flex-start;
}
.scc-msg-avatar {
  flex-shrink: 0;
  margin-top: 2px;
}
.scc-msg-avatar--user {
  background: linear-gradient(135deg, #00405e, #0097a7) !important;
  border: 2px solid rgba(0, 151, 167, 0.3);
}
.scc-msg-avatar--ai {
  background: linear-gradient(135deg, #6d28d9, #a855f7) !important;
  border: 2px solid rgba(124, 58, 237, 0.25);
}
.scc-msg-body {
  flex: 1;
  min-width: 0;
}
.scc-msg-author {
  font-size: 0.82rem;
  font-weight: 700;
  color: #37474f;
  margin-bottom: 4px;
  letter-spacing: 0.2px;
}
.scc-msg-content {
  font-size: 0.94rem;
  line-height: 1.7;
  color: #424242;
  word-break: break-word;
}

/* Thinking dots */
.scc-thinking {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 28px;
}
.scc-thinking-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0097a7, #6d28d9);
  animation: scc-bounce 1.4s infinite ease-in-out;
}
.scc-thinking-dot:nth-child(2) {
  animation-delay: 0.16s;
}
.scc-thinking-dot:nth-child(3) {
  animation-delay: 0.32s;
}
@keyframes scc-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* ═══════════════════════════════════════════
   EMPTY STATE
   ═══════════════════════════════════════════ */
.scc-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
}
.scc-empty-inner {
  max-width: 560px;
}
.scc-empty-orb {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  margin-bottom: 8px;
}
.scc-empty-orb-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(from 0deg, #0097a7, #6d28d9, #0097a7);
  opacity: 0.18;
  animation: scc-spin 6s linear infinite;
}
@keyframes scc-spin {
  to {
    transform: rotate(360deg);
  }
}
.scc-empty-orb-icon {
  position: relative;
  z-index: 1;
  color: #6d28d9 !important;
  background: linear-gradient(135deg, #ede9fe 0%, #e0f7fa 100%);
  border-radius: 50%;
  padding: 20px;
}
.scc-empty-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a2332;
  margin-bottom: 8px;
}
.scc-empty-subtitle {
  font-size: 0.95rem;
  color: #78909c;
  margin-bottom: 28px;
  line-height: 1.6;
}

/* Prompt Cards */
.scc-prompts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.scc-prompt-card {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid #e3eaf1;
  background: #f8fafc;
  cursor: pointer;
  text-align: left;
  font-size: 0.88rem;
  color: #37474f;
  line-height: 1.45;
  transition: all 0.2s;
}
.scc-prompt-card:hover {
  background: #e0f7fa;
  border-color: #b2ebf2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 151, 167, 0.1);
}

/* ═══════════════════════════════════════════
   CREATE SAC HINT
   ═══════════════════════════════════════════ */
.scc-create-hint {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 820px;
  margin: 0 auto 8px;
  padding: 10px 14px;
  font-size: 0.82rem;
  color: #546e7a;
}
.scc-suggestion-chip {
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border-radius: 20px;
  border: 1.5px solid #b2ebf2;
  background: #f0fdfe;
  color: #006494;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.scc-suggestion-chip:hover {
  background: #e0f7fa;
  border-color: #0097a7;
  color: #00405e;
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0, 151, 167, 0.15);
}

/* ═══════════════════════════════════════════
   INPUT AREA
   ═══════════════════════════════════════════ */
.scc-input-area {
  padding: 0 24px 16px;
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
}
.scc-input-card {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: #f8fafc;
  border: 1.5px solid #e3eaf1;
  border-radius: 16px;
  padding: 6px 6px 6px 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.scc-input-card:focus-within {
  border-color: #0097a7;
  box-shadow: 0 0 0 3px rgba(0, 151, 167, 0.1);
}
.scc-input-field {
  flex: 1;
}
.scc-input-field >>> .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  min-height: 36px !important;
}
.scc-input-field >>> textarea {
  font-size: 0.92rem;
  line-height: 1.5;
  color: #37474f;
  max-height: 150px;
}
.scc-input-field >>> .v-text-field__details {
  display: none;
}
.scc-send-btn {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: none;
  background: #e0e0e0;
  color: #9e9e9e;
  cursor: not-allowed;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
  margin-bottom: 2px;
}
.scc-send-btn--active {
  background: linear-gradient(135deg, #00405e 0%, #0097a7 100%);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 151, 167, 0.3);
}
.scc-send-btn--active:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 14px rgba(0, 151, 167, 0.4);
}
.scc-input-hint {
  text-align: center;
  font-size: 0.72rem;
  color: #b0bec5;
  margin-top: 8px;
}

/* ═══════════════════════════════════════════
   SAC DISPLAY
   ═══════════════════════════════════════════ */
.scc-sac-section {
  max-width: 820px;
  margin: 16px auto 24px;
  padding: 0 24px;
}
.scc-sac-card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(0, 64, 94, 0.12);
  box-shadow: 0 4px 20px rgba(0, 64, 94, 0.08);
}
.scc-sac-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: filter 0.2s;
}
.scc-sac-header:hover {
  filter: brightness(1.1);
}
.scc-sac-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.scc-sac-header-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
}
.scc-sac-header-title {
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.3px;
}
.scc-sac-body {
  padding: 20px;
  background: #fafcfe;
}

/* Claim Panels */
.scc-claim-panel {
  margin-bottom: 10px;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid #e8ecf1 !important;
}
.scc-claim-header {
  background: #fff !important;
  padding: 14px 16px !important;
  min-height: 56px !important;
}
.scc-claim-header-row {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
}
.scc-claim-badge {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.scc-claim-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}
.scc-claim-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #1976d2;
}
.scc-claim-preview {
  font-size: 0.9rem;
  color: #455a64;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}
.scc-claim-save-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 7px 14px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #388e3c, #66bb6a);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.scc-claim-save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(56, 142, 60, 0.3);
}
.scc-claim-save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.scc-claim-content {
  background: #f8fafc !important;
  padding: 18px !important;
}
.scc-claim-text {
  font-size: 0.95rem;
  line-height: 1.65;
  color: #37474f;
  padding: 14px;
  background: #fff;
  border-radius: 10px;
  border-left: 3px solid #1976d2;
  margin-bottom: 12px;
}

/* Section Labels */
.scc-section-label {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 0.82rem;
  color: #546e7a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

/* Argument Cards */
.scc-argument-card {
  display: flex;
  gap: 8px;
  background: #fff;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 8px;
  border-left: 3px solid #7b1fa2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.scc-argument-text {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #455a64;
}

/* Evidence */
.scc-evidence-box {
  background: linear-gradient(135deg, #f0f9ff 0%, #f1f8e9 100%);
  border-radius: 10px;
  padding: 12px;
  border-left: 3px solid #388e3c;
  margin-left: 16px;
}
.scc-evidence-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.78rem;
  color: #546e7a;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.scc-evidence-chips {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.scc-evidence-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.scc-ev-chip {
  font-size: 0.82rem !important;
  border-radius: 8px !important;
  height: auto !important;
  min-height: 28px !important;
}
.scc-ev-type-badge {
  height: 20px !important;
  font-size: 0.65rem !important;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  border-radius: 6px !important;
}

/* SAC Actions */
.scc-sac-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e8ecf1;
}
.scc-sac-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  white-space: nowrap;
}
.scc-sac-btn--save {
  background: linear-gradient(135deg, #388e3c, #66bb6a);
  color: #fff;
  box-shadow: 0 2px 8px rgba(56, 142, 60, 0.25);
}
.scc-sac-btn--save:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(56, 142, 60, 0.35);
}
.scc-sac-btn--save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.scc-sac-btn--outline {
  background: #fff;
  color: #37474f;
  border: 1px solid #e0e0e0;
}
.scc-sac-btn--outline:hover {
  background: #f5f5f5;
  border-color: #bdbdbd;
}

/* ═══════════════════════════════════════════
   MESSAGE CONTENT FORMATTING
   ═══════════════════════════════════════════ */
.scc-msg-content >>> .ai-header {
  margin: 16px 0 8px;
  color: #1a2332;
  font-weight: 700;
}
.scc-msg-content >>> h1.ai-header {
  font-size: 1.6rem;
  border-bottom: 1px solid #e8ecf1;
  padding-bottom: 8px;
}
.scc-msg-content >>> h2.ai-header {
  font-size: 1.35rem;
}
.scc-msg-content >>> h3.ai-header {
  font-size: 1.15rem;
}
.scc-msg-content >>> .ai-list {
  margin: 8px 0;
  padding-left: 20px;
}
.scc-msg-content >>> .ai-list-item {
  margin-bottom: 4px;
  line-height: 1.6;
}
.scc-msg-content >>> .ai-blockquote {
  border-left: 4px solid #0097a7;
  padding: 8px 16px;
  margin: 16px 0;
  background: #f0f9fa;
  border-radius: 0 8px 8px 0;
  font-style: italic;
  color: #455a64;
}
.scc-msg-content >>> .ai-hr {
  border: 0;
  border-top: 1px solid #e8ecf1;
  margin: 16px 0;
}

/* Code */
.scc-msg-content >>> .code-block {
  background: #1e1e2e;
  color: #cdd6f4;
  padding: 16px;
  border-radius: 12px;
  font-family: 'JetBrains Mono', 'Fira Code', Consolas, monospace;
  font-size: 0.85rem;
  line-height: 1.6;
  white-space: pre-wrap;
  margin: 12px 0;
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.scc-msg-content >>> code {
  background: #f0f4f8;
  padding: 2px 6px;
  border-radius: 6px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.88em;
  color: #c62828;
}

/* Tables */
.scc-msg-content >>> .table-container {
  margin: 16px 0;
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid #e8ecf1;
}
.scc-msg-content >>> .ai-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.scc-msg-content >>> .ai-table th {
  background: linear-gradient(135deg, #f5f7fa, #e8ecf1);
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.76rem;
  letter-spacing: 0.5px;
  color: #455a64;
  padding: 10px 14px;
  border-bottom: 2px solid #cfd8dc;
}
.scc-msg-content >>> .ai-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #eceff1;
  color: #455a64;
}
.scc-msg-content >>> .ai-table tr:hover td {
  background: #f5f9ff;
}
.scc-msg-content >>> .status-success {
  background: rgba(56, 142, 60, 0.06);
}
.scc-msg-content >>> .status-warning {
  background: rgba(245, 124, 0, 0.06);
}
.scc-msg-content >>> .status-error {
  background: rgba(198, 40, 40, 0.06);
}

/* JSON */
.scc-msg-content >>> .json-block {
  background: #1e1e2e;
  color: #cdd6f4;
  padding: 16px;
  border-radius: 12px;
  margin: 12px 0;
  overflow-x: auto;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.scc-msg-content >>> .json-key {
  color: #89b4fa;
}
.scc-msg-content >>> .json-string {
  color: #a6e3a1;
}
.scc-msg-content >>> .json-number {
  color: #fab387;
}
.scc-msg-content >>> .json-boolean {
  color: #89dceb;
}
.scc-msg-content >>> .json-null {
  color: #f38ba8;
}

/* ═══════════════════════════════════════════
   RESPONSIVE
   ═══════════════════════════════════════════ */
@media (max-width: 900px) {
  .scc-sidebar {
    width: 220px;
    min-width: 220px;
  }
  .scc-prompts {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 600px) {
  .scc-sidebar {
    width: 0;
    min-width: 0;
    overflow: hidden;
  }
  .scc-input-area {
    padding: 0 12px 12px;
  }
  .scc-messages-inner {
    padding: 12px;
  }
  .scc-sac-section {
    padding: 0 12px;
  }
}

/* ═══════════════════════════════════════════
   HITL (Human-in-the-Loop) Actions
   ═══════════════════════════════════════════ */
.scc-hitl-actions {
  margin: 8px 0 4px 46px;
  padding: 14px 18px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 12px;
}
.scc-hitl-banner {
  display: flex;
  align-items: center;
  font-size: 0.88rem;
  color: #5d4037;
  margin-bottom: 12px;
}
.scc-hitl-btns {
  display: flex;
  gap: 10px;
}
.scc-hitl-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}
.scc-hitl-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.scc-hitl-btn--approve {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}
.scc-hitl-btn--approve:hover:not(:disabled) {
  background: #c8e6c9;
}
.scc-hitl-btn--reject {
  background: #fce4ec;
  color: #c62828;
  border: 1px solid #ef9a9a;
}
.scc-hitl-btn--reject:hover:not(:disabled) {
  background: #f8bbd0;
}
.scc-hitl-reject-input {
  margin-top: 8px;
}
.scc-hitl-btn--reject-confirm {
  background: #c62828;
  color: #fff;
  border: none;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.scc-hitl-btn--reject-confirm:hover:not(:disabled) {
  background: #b71c1c;
}

/* ═══════════════════════════════════════════
   Sidebar chat item with date
   ═══════════════════════════════════════════ */
.scc-chat-item-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.scc-chat-item-date {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 1px;
}
.scc-chat-item--active .scc-chat-item-date {
  color: rgba(255, 255, 255, 0.55);
}
</style>
